import React, { useCallback, useRef, useState } from 'react';
import { Icon } from './ui';

// Accessible drag-and-drop file input.
// Validates file size BEFORE calling onFile — callers never receive an oversized file.
// Keyboard accessible: Enter / Space activates the hidden <input type="file">.

interface DropzoneFieldProps {
  /** Passed to the hidden <input type="file"> accept attribute, e.g. ".pdf" or ".csv". */
  accept?: string;
  /** Maximum allowed file size in bytes. Violations surface an inline error. */
  maxSizeBytes: number;
  /** Called only when the file passes size validation. */
  onFile: (file: File) => void;
  label: string;
  testId?: string;
  /** data-testid applied to the hidden <input type="file"> element for selector registries. */
  inputTestId?: string;
  disabled?: boolean;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

export const DropzoneField: React.FC<DropzoneFieldProps> = ({
  accept,
  maxSizeBytes,
  onFile,
  label,
  testId,
  inputTestId,
  disabled = false,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      if (file.size > maxSizeBytes) {
        setSizeError(
          `File is too large (${formatBytes(file.size)}). Maximum allowed is ${formatBytes(maxSizeBytes)}.`
        );
        return;
      }
      setSizeError(null);
      onFile(file);
    },
    [maxSizeBytes, onFile]
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset value so re-selecting the same file fires onChange again.
    e.target.value = '';
  };

  const activate = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div className={className}>
      <div
        data-testid={testId}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={`${label} — drop zone`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={activate}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            activate();
          }
        }}
        className={[
          'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed',
          'px-6 py-8 text-center transition-colors select-none',
          dragOver
            ? 'border-primary bg-[#EAF8F4]'
            : 'border-border bg-[#f8faf9] hover:border-primary/60 hover:bg-white',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Icon
          name="Upload"
          className={`w-8 h-8 ${dragOver ? 'text-primary' : 'text-text-tertiary'}`}
        />
        <div>
          <p className="text-sm font-medium text-irongrey">
            {dragOver ? 'Drop to upload' : label}
          </p>
          <p className="text-xs text-text-secondary mt-0.5">
            {accept
              ? `${accept
                  .split(',')
                  .map((s) => s.trim().replace(/^\./, '').toUpperCase())
                  .join(', ')} · `
              : ''}
            Up to {formatBytes(maxSizeBytes)}
          </p>
        </div>
      </div>

      {/* Hidden native file input — activated programmatically */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        data-testid={inputTestId}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {sizeError && (
        <div
          role="alert"
          className="mt-2 flex items-center gap-1.5 text-xs text-strawberry"
        >
          <Icon name="AlertCircle" className="w-3.5 h-3.5 shrink-0" />
          {sizeError}
        </div>
      )}
    </div>
  );
};
